import React, { useState, useMemo } from 'react';
import { Box, Typography, Collapse } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { green, grey, blue, purple, yellow, orange, red } from '@mui/material/colors';
import { Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

// CustomTooltip definition
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: 1,
          p: 1,
        }}
      >
        {/* <Typography variant="body2" sx={{ color: grey[900] }}>
          {payload[0].name}
        </Typography> */}
        <Typography variant="body2" sx={{ color: grey[900] }}>
          {payload[0].value}
        </Typography>
      </Box>
    );
  }
  return null;
};

const MatchSummary = ({ matchCount, set_width=undefined, show_title = true, show_dei = true, show_matches = true, show_legend = false }) => {
  const [expanded, setExpanded] = useState(true);

  const handleExpandToggle = () => setExpanded((prev) => !prev);

  const ethnicData = useMemo(() => {
    if (!matchCount || !matchCount.count_by_queries) return [];
    const { count_by_queries } = matchCount;

    return [
      { name: 'African American', value: count_by_queries['cmetrics-african american'] || 0, color: blue[400] },
      { name: 'Caucasian', value: count_by_queries['cmetrics-caucasian'] || 0, color: blue[200] },
      { name: 'Asian', value: count_by_queries['cmetrics-east asian'] || 0, color: blue[300] },
      { name: 'Hispanic', value: count_by_queries['cmetrics-hispanic'] || 0, color: green[300] },
    ];
  }, [matchCount]);

  const genderData = useMemo(() => {
    if (!matchCount || !matchCount.count_by_queries) return [];
    const { count_by_queries, base_filter_count } = matchCount;

    const female = count_by_queries['cmetrics-female'] || 0;
    const male = count_by_queries['cmetrics-male'] || 0;

    return [
      { name: 'Women', value: female, color: blue[500] },
      { name: 'Men', value: male, color: purple[400] },
      { name: 'Unclassified', value: base_filter_count - (female + male), color: grey[300] },
    ];
  }, [matchCount]);

  const warmCandidateData = useMemo(() => {
    if (!matchCount || !matchCount.target_requirements || !matchCount.count_by_queries) return [];
    const { target_requirements, count_by_queries, base_filter_count } = matchCount;

    const data = [];
    target_requirements.forEach(({ macro_id, section_id }) => {
      const value = count_by_queries[macro_id] || 0;
      let label, color;

      switch (section_id) {
        case 'crm_to_match':
          label = 'CRM';
          color = blue[400];
          break;
        case 'alumni_to_match':
          label = 'Alumni';
          color = green[400];
          break;
        case 'current_employees_to_match':
          label = 'Employees';
          color = yellow[400];
          break;
        case 'connections_to_match':
          label = 'Employee Connections';
          color = purple[400];
          break;
        case 'ats_to_match':
          label = 'ATS';
          color = orange[400];
          break;
        default:
          return;
      }

      data.push({ label, value, color });
    });

    data.push({ label: 'Passive', value: base_filter_count, color: red[400] });
    return data;
  }, [matchCount]);

  const valueFormatter = (item) => `${item.value}`;

  if (!matchCount) {
    return <div>Loading...</div>
  }

  return (
    <Box
      sx={{
        border: '1px solid #374151',
        borderRadius: 1,
        backgroundColor: '#1F2937',
        overflow: 'hidden',
      }}
      width={set_width ? set_width : 'auto'}
    >
      {show_title && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            p: 2,
            borderBottom: expanded ? '1px solid #374151' : 'none',
          }}
          onClick={handleExpandToggle}
        >
          <Typography variant="body1" fontWeight="medium" sx={{ color: '#fff' }}>
            Match Summary & Analytics
          </Typography>
          {expanded ? <ExpandLess sx={{ color: '#fff' }} /> : <ExpandMore sx={{ color: '#fff' }} />}
        </Box>
      )}

      <Collapse in={!show_title || expanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 2 }}>
          {show_dei && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
                Ethnic Diversity
              </Typography>
              <ResponsiveContainer width="100%" height={70}>
                <BarChart data={ethnicData}>
                  <XAxis dataKey="name" style={{ fontSize: '0.7rem', fill: '#fff' }} tickLine={false} />
                  <YAxis hide={true} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value">
                    {ethnicData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              <Typography variant="body1" sx={{ color: '#fff', mt: 2, mb: 1 }}>
                Gender Diversity
              </Typography>
              <ResponsiveContainer width="100%" height={70}>
                <BarChart data={genderData}>
                  <XAxis dataKey="name" style={{ fontSize: '0.7rem', fill: '#fff' }} tickLine={false} />
                  <YAxis hide={true} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value">
                    {genderData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}

          {show_matches && (
            <Box>
              <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
                Candidate Pool (Total: {matchCount ? matchCount.base_filter_count : 0})
              </Typography>
              {warmCandidateData && warmCandidateData.length > 0 && (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart
                    series={[
                      {
                        data: warmCandidateData,
                        valueFormatter,
                      },
                    ]}
                    slotProps={{ legend: { hidden: !show_legend } }}
                    height={400}
                  />
                </ResponsiveContainer>
              )}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default MatchSummary;